<template>
    <transition name="fade">
      <div v-if="visible" class="fixed" @click.stop>
        <div class="bg-white popup">
          <div class="popup-header">
            <h1 class="title">Añadir usuarios a {{ product.titulo }}</h1>
            <img src="@/assets/images/ic_close.svg" class="close_icon" @click="closePopup()" />
          </div>
          
          <div class="popup-body">
            <h4 class="subtitle">Quedan {{ product.stock_actual }} plazas libres en este producto.</h4>

            <div class="form-row">
              <div class="input-group half">
                <label>Email del usuario*</label>
                <input
                  type="text"
                  v-model="userEmail"/>
              </div>
              <div class="input-group half">
                <label>Numero de plazas*</label>
                <input
                  type="number"
                  step="1"
                  v-model="plazas"/>
              </div>
            </div>
          </div>

          <div class="popup-footer">
            <button @click="closePopup()" class="secondary_button">Cancelar</button>
            <button @click="saveClicked()" class="main_button">Añadir participante</button>
          </div>
        </div>
      </div>
    </transition>
  </template>
  
  <script setup>
  import { ref, watch, defineProps, defineEmits } from 'vue';
  import { useLoadingStore } from "@/stores/loadingStore";
  import { refreshToken } from "@/services/authService";
  import { useRouter } from 'vue-router';

  const props = defineProps({
    product: Object,
  });
  
  const emit = defineEmits(['userAddedToProduct', 'userAddedToProductPopupCancelled', 'userAddedToProductErrorHappened']);
  const loadingStore = useLoadingStore();
  const router = useRouter();

  const visible = ref(false);
  const currentProduct = ref(null);
  const userEmail = ref("");
  const plazas = ref(0);

  watch(() => props.product, (newVal) => {
    currentProduct.value = newVal;
    visible.value = !!newVal;
  });
  
  const closePopup = () => {
    visible.value = false;
    currentProduct.value = null;
    userEmail.value = "";
    plazas.value = 0;
    emit('userAddedToProductPopupCancelled');
  };

  const saveClicked = async () => {
    if (userEmail.value.trim() === ""
      || (plazas.value === null || plazas.value === undefined || isNaN(plazas.value))) {
      emit('userAddedToProductErrorHappened', 'Por favor, completa todos los campos obligatorios');
      return;
    }

    loadingStore.setLoading(true);

    try {
      let response = await addUserToProductInAPI();

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await addUserToProductInAPI();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        emit('userAddedToProductErrorHappened', 'Se ha producido un error añadiendo el usuario al plan.');
        return;
      }

      const data = await response.json();

      if (data.errors) {
        emit('userAddedToProductErrorHappened', data.message);
      } else {
        emit('userAddedToProduct', currentProduct.value.id_producto_promo, data.datos.new_product_stock);
        closePopup();
      }
    } catch (exception) {
      emit('userAddedToProductErrorHappened', exception.message);
    } finally {
      loadingStore.setLoading(false);
    }
  };

  const addUserToProductInAPI = async () => {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/plans/add_participant", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
        body: JSON.stringify({
          "user_id": localStorage.getItem('user_id'),
          "user_email": userEmail.value,
          "product_id": currentProduct.value.id_producto_promo,
          "amount": plazas.value
        }),
      });
  };
  </script>
  
  <style scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  
  .fixed {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }
  
  .bg-white {
    background-color: white;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .popup-header {
    padding: var(--spacing-md) var(--spacing-lg);
    border-bottom: 1px solid var(--light-gray);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .title {
    font-size: 20px;
    font-weight: 700;
    color: var(--text-color-dark);
    margin: 0;
  }

  .close_icon {
    position: absolute;
    right: var(--spacing-md);
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity var(--transition-fast);
  }

  .close_icon:hover {
    opacity: 1;
  }

  .popup-body {
    padding: var(--spacing-lg);
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .subtitle {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-color-dark);
    margin: 0 0 var(--spacing-md) 0;
  }

  .form-row {
    display: flex;
    gap: var(--spacing-md);
  }

  .input-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .input-group.half {
    flex: 1;
  }

  label {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-color-dark);
  }

  input {
    width: 100%;
    box-sizing: border-box;
    padding: 10px;
    border: 1px solid var(--gray);
    border-radius: var(--radius-md);
    font-family: inherit;
    font-size: 14px;
    transition: border-color var(--transition-fast);
  }

  input:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  .popup-footer {
    padding: var(--spacing-md) var(--spacing-lg);
    border-top: 1px solid var(--light-gray);
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-md);
    background-color: var(--white);
  }

  .secondary_button, .main_button {
    width: auto;
    min-width: 120px;
  }
</style>
  